import type { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import FileDropzone from '@v2/components/ui/FileDropzone';
import toast from 'react-hot-toast';

export type RequirementSchema = {
    key: string;
    type: string;
    label: string;
    required?: boolean;
    options?: Record<string, string> | string[];
};

type Props = {
    schema: RequirementSchema[];
    register: UseFormRegister<Record<string, unknown>>;
    setValue?: UseFormSetValue<Record<string, unknown>>;
    values?: Record<string, unknown>;
};

export default function RequirementFieldRenderer({ schema, register, setValue }: Props) {
    return (
        <div className="space-y-4">
            {schema.map((field) => (
                <div key={field.key}>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                        {field.label}
                        {field.required && <span className="text-red-500"> *</span>}
                    </label>
                    {renderField(field, register, setValue)}
                </div>
            ))}
        </div>
    );
}

function renderField(
    field: RequirementSchema,
    register: UseFormRegister<Record<string, unknown>>,
    setValue?: UseFormSetValue<Record<string, unknown>>,
) {
    const common = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm';

    switch (field.type) {
        case 'textarea':
            return <textarea {...register(field.key)} rows={3} className={common} />;
        case 'date':
            return <input type="date" {...register(field.key)} className={common} />;
        case 'number':
            return <input type="number" step="any" {...register(field.key)} className={common} />;
        case 'checkbox':
            return <input type="checkbox" {...register(field.key)} className="h-4 w-4 rounded border-gray-300" />;
        case 'select': {
            const options = Array.isArray(field.options)
                ? field.options.map((o) => [o, o])
                : Object.entries(field.options ?? {});
            return (
                <select {...register(field.key)} className={common}>
                    <option value="">—</option>
                    {options.map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
            );
        }
        case 'file':
        case 'files': {
            const multiple = field.type === 'files';
            if (!setValue) {
                return <input type="file" {...register(field.key)} className={common} multiple={multiple} />;
            }
            return (
                <FileDropzone
                    multiple={multiple}
                    maxSizeMb={20}
                    label={multiple ? 'Arraste os arquivos aqui' : 'Arraste o arquivo aqui'}
                    hint="ou clique para selecionar"
                    onFiles={(files) => {
                        setValue(field.key, multiple ? files : files[0], { shouldDirty: true, shouldValidate: true });
                    }}
                    onError={(msg) => toast.error(msg)}
                />
            );
        }
        default:
            return <input type="text" {...register(field.key)} className={common} />;
    }
}
